import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignIn from './GoogleSignIn';
import AppleSignIn from './AppleSignIn';

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword: () => void;
  onClose?: () => void;
  onLoginSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister, onSwitchToForgotPassword, onClose, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, googleLogin, appleLogin } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (result.success) {
      if (onLoginSuccess) onLoginSuccess();
      if (onClose) onClose();
    } else {
      setError(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  const handleGoogleSuccess = async (credential: string) => {
    setLoading(true);
    setError('');

    const result = await googleLogin(credential);
    
    if (result.success) {
      if (onLoginSuccess) onLoginSuccess();
      if (onClose) onClose();
    } else {
      setError(result.error || 'Google login failed');
    }
    
    setLoading(false);
  };

  const handleGoogleError = (error: string) => {
    // Provide more helpful error messages for common issues
    let userFriendlyError = error;
    
    if (error.toLowerCase().includes('eof') || error.toLowerCase().includes('network')) {
      userFriendlyError = 'Network connection issue. Please try again or use email/password to sign in.';
    }
    
    setError(userFriendlyError);
  };

  const handleAppleSuccess = async (response: any) => {
    setLoading(true);
    setError('');

    const result = await appleLogin(response.identityToken, response.user);
    
    if (result.success) {
      if (onLoginSuccess) onLoginSuccess();
      if (onClose) onClose();
    } else {
      setError(result.error || 'Apple login failed');
    }
    
    setLoading(false);
  };

  const handleAppleError = (error: string) => {
    setError(error);
  };

  return (
    <div className="auth-form">
      <h2>Sign In</h2>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="submit-btn"
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <AppleSignIn
        onSuccess={handleAppleSuccess}
        onError={handleAppleError}
        text="Sign in with Apple"
      />

      <GoogleSignIn
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        text="Sign in with Google"
      />

      <div className="switch-mode">
        <p>
          Don't have an account?{' '}
          <button onClick={onSwitchToRegister}>
            Sign up
          </button>
        </p>
        <p>
          <button onClick={onSwitchToForgotPassword} className="forgot-password-link">
            Forgot your password?
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
